import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ShoppingBag, Loader2, Minus, Plus } from "lucide-react";
import { fetchProductByHandle, ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { CartDrawer } from "@/components/CartDrawer";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";
import { useCurrency } from "@/hooks/useCurrency";
import SEO from "@/components/SEO";

// Import t-shirt variant images
import teeWhite from "@/assets/tee-hormones-weaponised-white.jpg";
import teeChambray from "@/assets/tee-hormones-weaponised-chambray.jpg";
import teeCrimson from "@/assets/tee-hormones-weaponised-crimson.jpg";
import teeCrunchberry from "@/assets/tee-hormones-weaponised-crunchberry.jpg";
import teeGreen from "@/assets/tee-hormones-weaponised-folded-green.jpg";
import teeSalmon from "@/assets/tee-hormones-weaponised-folded-salmon.png";
import teeTerracotta from "@/assets/tee-hormones-weaponised-folded-terracotta.jpg";
import teeOrchid from "@/assets/tee-hormones-weaponised-orchid.jpg";

// Import hormones mug images
import mugHormonesBlack from "@/assets/mug-hormones-black.jpg";
import mugHormonesNavy from "@/assets/mug-hormones-navy.jpg";
import mugHormonesCambridgeBlue from "@/assets/mug-hormones-cambridge-blue.jpg";
import mugHormonesGreen from "@/assets/mug-hormones-green.jpg";
import mugHormonesGoldenYellow from "@/assets/mug-hormones-golden-yellow.png";
import mugHormonesLightBlue from "@/assets/mug-hormones-light-blue.png";
import mugHormonesLightGreen from "@/assets/mug-hormones-light-green-new.png";
import mugHormonesMaroon from "@/assets/mug-hormones-maroon.png";
import mugHormonesOrange from "@/assets/mug-hormones-orange.png";
import mugHormonesPink from "@/assets/mug-hormones-pink-new.png";
import mugHormonesRed from "@/assets/mug-hormones-red-new.png";
import mugHormonesYellow from "@/assets/mug-hormones-yellow-new.png";

// Import surviving and caffeinating mug images (context images with design)
import mugSurvivingBlack from "@/assets/mug-surviving-caffeinating-black-new.jpg";
import mugSurvivingNavy from "@/assets/mug-surviving-caffeinating-navy-new.jpg";
import mugSurvivingCambridgeBlue from "@/assets/mug-surviving-caffeinating-cambridge-blue-new.jpg";
import mugSurvivingGreen from "@/assets/mug-surviving-caffeinating-green-new.jpg";
import mugSurvivingGoldenYellow from "@/assets/mug-surviving-caffeinating-golden-yellow.jpg";
import mugSurvivingLightBlue from "@/assets/mug-surviving-caffeinating-light-blue.jpg";
import mugSurvivingLightGreen from "@/assets/mug-surviving-caffeinating-light-green.jpg";
import mugSurvivingMaroon from "@/assets/mug-surviving-caffeinating-maroon-new.jpg";
import mugSurvivingOrange from "@/assets/mug-surviving-caffeinating-orange.jpg";
import mugSurvivingPink from "@/assets/mug-surviving-caffeinating-pink.jpg";
import mugSurvivingRed from "@/assets/mug-surviving-caffeinating-red.jpg";
import mugSurvivingYellow from "@/assets/mug-surviving-caffeinating-yellow-new.jpg";

// Color mapping for mug variants
const COLOR_MAP: Record<string, string> = {
  'black': '#000000',
  'navy': '#312783',
  'cambridge blue': '#4A90E2',
  'green': '#1B5E45',
  'golden yellow': '#F79431',
  'light blue': '#29ABE2',
  'light green': '#8BC34A',
  'maroon': '#6B2C3E',
  'orange': '#FF5722',
  'pink': '#F8B4D9',
  'red': '#DC143C',
  'yellow': '#F4D03F',
};

// Image mapping for t-shirt variants
const TEE_IMAGE_MAP: Record<string, string> = {
  'white': teeWhite,
  'chambray': teeChambray,
  'crimson': teeCrimson,
  'crunchberry': teeCrunchberry,
  'light green': teeGreen,
  'bright salmon': teeSalmon,
  'terracotta': teeTerracotta,
  'orchid': teeOrchid,
};

// Image mapping for hormones mug variants
const HORMONES_MUG_IMAGE_MAP: Record<string, string> = {
  'black': mugHormonesBlack,
  'navy': mugHormonesNavy,
  'navy blue': mugHormonesNavy,
  'cambridge blue': mugHormonesCambridgeBlue,
  'green': mugHormonesGreen,
  'golden yellow': mugHormonesGoldenYellow,
  'light blue': mugHormonesLightBlue,
  'light green': mugHormonesLightGreen,
  'maroon': mugHormonesMaroon,
  'orange': mugHormonesOrange,
  'pink': mugHormonesPink,
  'red': mugHormonesRed,
  'yellow': mugHormonesYellow,
};

// Image mapping for surviving and caffeinating mug variants
const SURVIVING_MUG_IMAGE_MAP: Record<string, string> = {
  'black': mugSurvivingBlack,
  'navy': mugSurvivingNavy,
  'navy blue': mugSurvivingNavy,
  'cambridge blue': mugSurvivingCambridgeBlue,
  'green': mugSurvivingGreen,
  'golden yellow': mugSurvivingGoldenYellow,
  'light blue': mugSurvivingLightBlue,
  'light green': mugSurvivingLightGreen,
  'maroon': mugSurvivingMaroon,
  'orange': mugSurvivingOrange,
  'pink': mugSurvivingPink,
  'red': mugSurvivingRed,
  'yellow': mugSurvivingYellow,
};

const KNOWN_COLOR_KEYS = [
  'black','navy','navy blue','cambridge blue','green','golden yellow',
  'light blue','light green','maroon','orange','pink','red','yellow'
];

// Match longer names first to avoid substring collisions (e.g., "green" vs "light green")
const SORTED_COLOR_KEYS = [...KNOWN_COLOR_KEYS].sort((a, b) => b.length - a.length);

const aliasColor = (name: string): string => {
  let n = name.toLowerCase().trim();
  if (n.startsWith('navy')) n = 'navy';
  if (n.startsWith('cambridge')) n = 'cambridge blue';
  if (n === 'golden' || n.startsWith('golden ')) n = 'golden yellow';
  return n;
};

const extractAccentColorFromText = (text: string | undefined): string | undefined => {
  if (!text) return undefined;
  const s = text.toLowerCase();
  const m = s.match(/with\s+(.+?)\s+accent/);
  if (m?.[1]) return aliasColor(m[1]);
  for (const key of SORTED_COLOR_KEYS) {
    if (s.includes(key)) return aliasColor(key);
  }
  return undefined;
};

const getVariantAccentColor = (variant: ShopifyProduct['node']['variants']['edges'][number]['node']): string | undefined => {
  const fromOption = variant.selectedOptions.find(o => /(colou?r|accent)/i.test(o.name))?.value;
  const tryFromAny = variant.selectedOptions.map(o => o.value).find(v => extractAccentColorFromText(v));
  return extractAccentColorFromText(fromOption) || extractAccentColorFromText(variant.title) || extractAccentColorFromText(tryFromAny);
};

const getVariantColorKey = (variant: ShopifyProduct['node']['variants']['edges'][number]['node']): string | undefined => {
  const colorOption = variant.selectedOptions.find(o => /colou?r/i.test(o.name))?.value;
  if (!colorOption) return undefined;
  
  // Normalize the color name to match our image map keys
  let normalized = colorOption.toLowerCase().trim();
  
  // Handle exact Shopify color name matches
  const colorMap: Record<string, string> = {
    'black': 'black',
    'navy': 'navy',
    'cambridge blue': 'cambridge blue',
    'green': 'green',
    'golden yellow': 'golden yellow',
    'light blue': 'light blue',
    'light green': 'light green',
    'light-blue': 'light blue',
    'light-green': 'light green',
    'maroon': 'maroon',
    'orange': 'orange',
    'pink': 'pink',
    'red': 'red',
    'yellow': 'yellow',
    'white': 'white',
    'chambray': 'chambray',
    'crimson': 'crimson',
    'crunchberry': 'crunchberry',
    'orchid': 'orchid',
    'salmon': 'salmon',
    'terracotta': 'terracotta',
  };
  
  return colorMap[normalized] || normalized;
};

const getProductImageForVariant = (variant: ShopifyProduct['node']['variants']['edges'][number]['node'], productHandle?: string): string | undefined => {
  const isMug = productHandle?.includes('mug') ||
    productHandle?.includes('surviving') ||
    productHandle?.includes('hormones');
  const isTee = productHandle?.includes('t-shirt') || productHandle?.includes('tee');
  
  if (isTee) {
    const colorKey = getVariantColorKey(variant);
    return colorKey ? TEE_IMAGE_MAP[colorKey] : undefined;
  }
  
  if (isMug) {
    // First try direct color option (for Surviving mug and others)
    let color = getVariantColorKey(variant);
    
    // If no direct color, try accent color extraction (for Hormones mug with "White with X Accent" naming)
    if (!color) {
      color = getVariantAccentColor(variant);
    }
    
    if (!color) return undefined;
    
    const imageMap = productHandle?.includes('surviving') 
      ? SURVIVING_MUG_IMAGE_MAP 
      : HORMONES_MUG_IMAGE_MAP;
    
    return imageMap[color];
  }
  
  return undefined;
};

const ProductDetail = () => {
  const { handle } = useParams<{ handle: string }>();
  const navigate = useNavigate();
  const { convertPrice } = useCurrency();
  const [product, setProduct] = useState<ShopifyProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariantId, setSelectedVariantId] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedColor, setSelectedColor] = useState<string>("");
  const [selectedSize, setSelectedSize] = useState<string>("");
  const addItem = useCartStore(state => state.addItem);

  useEffect(() => {
    loadProduct();
  }, [handle]);

  const loadProduct = async () => {
    if (!handle) return;
    
    try {
      setLoading(true);
      
      // Try original handle first, then with -1 suffix if it fails
      let fetchedProduct = await fetchProductByHandle(handle);
      
      // If not found and handle doesn't end with a number, try with -1 suffix
      if (!fetchedProduct && !handle.match(/-\d+$/)) {
        fetchedProduct = await fetchProductByHandle(`${handle}-1`);
      }
      
      setProduct(fetchedProduct);
      
      if (fetchedProduct?.node.variants.edges.length > 0) {
        const firstVariant = fetchedProduct.node.variants.edges[0].node;
        setSelectedVariantId(firstVariant.id);
        
        // Set initial color and size from first variant
        const colorOption = firstVariant.selectedOptions.find(o => /color/i.test(o.name));
        const sizeOption = firstVariant.selectedOptions.find(o => /size/i.test(o.name));
        if (colorOption) setSelectedColor(colorOption.value);
        if (sizeOption) setSelectedSize(sizeOption.value);
        
        // Choose image strategy based on product type
        const localImage = getProductImageForVariant(firstVariant, fetchedProduct.node.handle);
        
        setSelectedImage(
          (localImage) || 
          firstVariant.image?.url || 
          fetchedProduct.node.images.edges[0]?.node.url || 
          ""
        );
      }
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error("Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  const findVariantByColorAndSize = (color: string, size: string) => {
    return product?.node.variants.edges.find(v => {
      const variantColor = v.node.selectedOptions.find(o => /colou?r/i.test(o.name))?.value;
      const variantSize = v.node.selectedOptions.find(o => /size/i.test(o.name))?.value;
      return variantColor === color && variantSize === size;
    })?.node;
  };

  const handleColorChange = (color: string, variantId: string) => {
    setSelectedColor(color);
    
    // Try to find variant with selected size, fallback to any variant with this color
    const matchingVariant = findVariantByColorAndSize(color, selectedSize);
    const variant = matchingVariant || product?.node.variants.edges.find(v => v.node.id === variantId)?.node;
    
    if (variant) {
      setSelectedVariantId(variant.id);
      
      // Update size if we switched to a different variant
      const sizeOption = variant.selectedOptions.find(o => /size/i.test(o.name));
      if (sizeOption) setSelectedSize(sizeOption.value);
      
      const localImage = getProductImageForVariant(variant, product?.node.handle);
      
      if (localImage) {
        setSelectedImage(localImage);
      } else if (variant.image?.url) {
        setSelectedImage(variant.image.url);
      } else if (product?.node.images.edges[0]?.node.url) {
        setSelectedImage(product.node.images.edges[0].node.url);
      }
    }
  };

  const handleSizeChange = (size: string) => {
    setSelectedSize(size);
    
    // Find variant with selected color and new size
    const variant = findVariantByColorAndSize(selectedColor, size);
    
    if (variant) {
      setSelectedVariantId(variant.id);
    }
  };

  const handleVariantChange = (variantId: string) => {
    setSelectedVariantId(variantId);
    const variant = product?.node.variants.edges.find(v => v.node.id === variantId)?.node;
    
    if (variant) {
      const localImage = getProductImageForVariant(variant, product?.node.handle);
      
      // Use local image if available, otherwise fall back to Shopify image
      if (localImage) {
        setSelectedImage(localImage);
      } else if (variant.image?.url) {
        setSelectedImage(variant.image.url);
      } else if (product?.node.images.edges[0]?.node.url) {
        setSelectedImage(product.node.images.edges[0].node.url);
      }
    }
  };

  const handleAddToCart = () => {
    if (!product || !selectedVariantId) return;

    const variant = product.node.variants.edges.find(
      v => v.node.id === selectedVariantId
    )?.node;

    if (!variant) return;

    const cartItem = {
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: quantity,
      selectedOptions: variant.selectedOptions
    };
    
    addItem(cartItem);
    toast.success("Added to cart", {
      description: `${product.node.title} - ${variant.title}`,
      position: "top-center"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Product not found</h2>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const selectedVariant = product.node.variants.edges.find(
    v => v.node.id === selectedVariantId
  )?.node;

  const images = product.node.images.edges;

  const productImage = images[0]?.node.url;
  const productDesc = (product.node.description || `${product.node.title} — from the Her Season shop.`).slice(0, 160);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex flex-col">
      <SEO
        title={product.node.title}
        description={productDesc}
        ogType="product"
        image={productImage}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.node.title,
          description: productDesc,
          image: productImage ? [productImage] : undefined,
          offers: selectedVariant
            ? {
                "@type": "Offer",
                price: parseFloat(selectedVariant.price.amount || "0").toFixed(2),
                priceCurrency: selectedVariant.price.currencyCode,
                availability: "https://schema.org/InStock",
              }
            : undefined,
        }}
      />
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/shop/mugs')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <p className="text-xl font-bold">Product Details</p>
          <CartDrawer />
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 flex-1">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Image Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <Card className="overflow-hidden">
              <div className="aspect-square bg-secondary/20">
                {selectedImage ? (
                  <img
                    src={selectedImage}
                    alt={product.node.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ShoppingBag className="h-20 w-20 text-muted-foreground" />
                  </div>
                )}
              </div>
            </Card>
            
            {/* Thumbnail Gallery - First 4 images */}
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {images.slice(0, 4).map((image, index) => (
                  <button
                    key={image.node.url}
                    onClick={() => setSelectedImage(image.node.url)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === image.node.url
                        ? 'border-primary ring-2 ring-primary/20'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <img
                      src={image.node.url}
                      alt={image.node.altText || `${product.node.title} view ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold mb-2 capitalize">{product.node.title}</h1>
              <div className="space-y-1">
                <div className="text-3xl font-bold text-primary">
                  {selectedVariant?.price.currencyCode} ${parseFloat(selectedVariant?.price.amount || '0').toFixed(2)}
                </div>
                <div className="text-lg text-muted-foreground">
                  AUD ${convertPrice(parseFloat(selectedVariant?.price.amount || '0')).toFixed(2)}
                </div>
              </div>
            </div>

            {/* Color Selector with Thumbnails */}
            {product.node.variants.edges.length > 1 && (
              <div className="space-y-4">
                <label className="text-sm font-semibold">Color</label>
                <div className="grid grid-cols-6 gap-3">
                  {(() => {
                    // Deduplicate variants by primary option (Color for apparel)
                    const primaryOptionName = product.node.options.find(o => /colou?r/i.test(o.name))?.name || product.node.options[0]?.name;
                    const seen = new Set<string>();

                    const uniqueVariants = product.node.variants.edges.filter((variant) => {
                      const key = (variant.node.selectedOptions.find(o =>
                        primaryOptionName ? o.name === primaryOptionName : true
                      )?.value || variant.node.title).toLowerCase();

                      if (seen.has(key)) return false;
                      seen.add(key);
                      return true;
                    });
                    
                    return uniqueVariants.map((variant) => {
                      const color = variant.node.selectedOptions.find(o => /colou?r/i.test(o.name))?.value || '';
                      const thumbnailImage = getProductImageForVariant(variant.node, product.node.handle) || 
                        variant.node.image?.url || 
                        product.node.images.edges[0]?.node.url;
                    
                      return (
                        <button
                          key={variant.node.id}
                          onClick={() => handleColorChange(color, variant.node.id)}
                          className={`group relative aspect-square rounded-lg overflow-hidden transition-all ${
                            selectedColor === color
                              ? 'ring-2 ring-primary ring-offset-2 ring-offset-background scale-105'
                              : 'hover:scale-105 ring-1 ring-border hover:ring-primary/50'
                          }`}
                          
                          title={color}
                        >
                          {thumbnailImage ? (
                            <img 
                              src={thumbnailImage}
                              alt={color}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-secondary/20 flex items-center justify-center">
                              <ShoppingBag className="h-8 w-8 text-muted-foreground" />
                            </div>
                          )}
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Size Selector */}
            {(() => {
              const sizeOption = product.node.options.find(o => /size/i.test(o.name));
              if (!sizeOption) return null;
              
              return (
                <div className="space-y-3">
                  <label className="text-sm font-semibold">Size</label>
                  <div className="flex gap-2 flex-wrap">
                    {sizeOption.values.map((size) => (
                      <Button
                        key={size}
                        variant={selectedSize === size ? "default" : "outline"}
                        size="lg"
                        onClick={() => handleSizeChange(size)}
                        className="min-w-[60px]"
                      >
                        {size}
                      </Button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Quantity Selector */}
            <div className="space-y-3">
              <label className="text-sm font-semibold">Quantity</label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="text-lg font-semibold w-12 text-center">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Button
              size="lg"
              className="w-full"
              onClick={handleAddToCart}
            >
              Add to Cart
            </Button>

            {/* Description */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">About This Product</h3>
              <p className="text-foreground/80 leading-relaxed whitespace-pre-line text-[15px] tracking-wide">
                {product.node.description
                  .replace(/15oz|15 oz/gi, '')
                  .replace(/Choose between the smaller 11 oz for single-cup sips or the roomier.*?when you need extra fuel\./gi, '')
                  .replace(/Available in 11 oz/gi, 'Available in a 11 oz')
                  .replace(/\s+/g, ' ')
                  .trim()}
              </p>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetail;
